/**
 * AI Proposal Review Dashboard page — Issue #394.
 *
 * Renders the unified read-model returned by GET
 * /api/v1/ai-proposal-review-dashboard. The page is strictly read-only;
 * actions (review / publish / retract) navigate to existing pages that own
 * the state transitions.
 */
import { useEffect } from 'react';
import { Link } from 'wouter';
import {
  useAiProposalReviewDashboard,
  type AiProposalReviewDashboardDto,
} from '@/hooks/useAiProposalReviewDashboard';

type Entry = AiProposalReviewDashboardDto['entries'][number];
type Counts = AiProposalReviewDashboardDto['counts'];

const ROLE_LABEL: Record<string, string> = {
  doctor: '医生',
  technical_lead: '技术负责人',
  compliance: '合规',
};

const STATUS_LABEL: Record<string, string> = {
  draft: '草稿',
  in_review: '审核中',
  approved: '已审核通过',
  rejected: '已驳回',
  locked: '已锁定',
  cancelled: '已取消',
};

const PUB_STATE_LABEL: Record<string, string> = {
  draft: '发布草稿',
  reviewed: '已审核待发布',
  published: '已发布',
  retracted: '已撤回',
};

export default function AiProposalReviewDashboardPage() {
  const { snapshot, loading, error, refresh } = useAiProposalReviewDashboard();

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">AI 方案草稿审核与客户发布看板</h1>
          <p className="text-sm text-muted-foreground">
            草稿 → 医生 → 技术 → 合规 → 客户发布；未审核内容不会出现在客户端，撤回后客户端即时不可见。
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={loading}
          className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
        >
          {loading ? '加载中…' : '刷新'}
        </button>
      </header>

      {error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error.message}
        </div>
      ) : null}

      {snapshot ? (
        <>
          <CountsPanel counts={snapshot.counts} />
          <section className="rounded-lg border border-border">
            <div className="border-b border-border px-4 py-3 text-sm font-medium">
              方案与发布状态（{snapshot.entries.length} 条）
            </div>
            {snapshot.entries.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                当前租户下暂无活跃 AI 方案草稿或审核中的方案。
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {snapshot.entries.map((entry) => (
                  <EntryRow key={entry.proposalId} entry={entry} />
                ))}
              </ul>
            )}
          </section>
          <p className="text-xs text-muted-foreground">
            生成时间：{new Date(snapshot.generatedAt).toLocaleString()}
          </p>
        </>
      ) : null}
    </div>
  );
}

function CountsPanel({ counts }: { counts: Counts }) {
  const tiles: Array<{ label: string; value: number; tone?: 'attention' | 'risk' }> = [
    { label: '待医生审核', value: counts.awaitingDoctor, tone: 'attention' },
    { label: '待技术负责人审核', value: counts.awaitingTechnicalLead, tone: 'attention' },
    { label: '待合规审核', value: counts.awaitingCompliance, tone: 'attention' },
    { label: '已审核待发布', value: counts.approvedNotPublished, tone: 'attention' },
    { label: '客户可见', value: counts.publishedCustomerVisible },
    { label: '已撤回（最近）', value: counts.retractedRecently, tone: 'risk' },
  ];
  return (
    <section className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      {tiles.map((t) => (
        <div
          key={t.label}
          className={
            'rounded-lg border px-4 py-3 ' +
            (t.tone === 'risk'
              ? 'border-destructive/40 bg-destructive/5'
              : t.tone === 'attention' && t.value > 0
                ? 'border-amber-500/40 bg-amber-500/5'
                : 'border-border')
          }
        >
          <div className="text-xs text-muted-foreground">{t.label}</div>
          <div className="mt-1 text-2xl font-semibold tabular-nums">{t.value}</div>
        </div>
      ))}
    </section>
  );
}

function EntryRow({ entry }: { entry: Entry }) {
  const seq = entry.reviewSequence;
  const pub = entry.customerPublication;
  return (
    <li className="px-4 py-3">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 flex-col">
          <Link href={`/proposals/${entry.proposalId}`}>
            <a className="truncate font-medium hover:underline">{entry.title}</a>
          </Link>
          <div className="text-xs text-muted-foreground">
            客户 {entry.customerId} · 方案状态 {STATUS_LABEL[entry.status] ?? entry.status}
            {entry.currentVersionNo !== null
              ? ` · 当前版本 v${entry.currentVersionNo}`
              : ''}
            {entry.currentVersionSource === 'ai_draft' ? ' · AI 草稿' : ''}
          </div>
        </div>
        <div className="flex flex-col items-start gap-1 md:items-end">
          <SequenceBadge
            blocked={seq.blocked}
            fullyApproved={seq.fullyApproved}
            nextRole={seq.nextRole}
          />
          <PublicationBadge pub={pub} />
        </div>
      </div>
      {entry.reviewsOnCurrentVersion.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-2 text-xs">
          {entry.reviewsOnCurrentVersion.map((r) => (
            <li
              key={`${r.role}-${r.reviewedAt}`}
              className={
                'rounded-md border px-2 py-0.5 ' +
                (r.decision === 'approved'
                  ? 'border-emerald-500/40 bg-emerald-500/10'
                  : 'border-destructive/40 bg-destructive/10')
              }
            >
              {ROLE_LABEL[r.role] ?? r.role}：
              {r.decision === 'approved' ? '通过' : r.decision === 'rejected' ? '驳回' : '需修改'}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function SequenceBadge({
  blocked,
  fullyApproved,
  nextRole,
}: {
  blocked: boolean;
  fullyApproved: boolean;
  nextRole: string | null;
}) {
  if (blocked) {
    return (
      <span className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-xs">
        审核中断（驳回 / 需修改）
      </span>
    );
  }
  if (fullyApproved) {
    return (
      <span className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs">
        三方审核已完成
      </span>
    );
  }
  if (nextRole) {
    return (
      <span className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs">
        待 {ROLE_LABEL[nextRole] ?? nextRole} 审核
      </span>
    );
  }
  return (
    <span className="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground">
      未提交审核
    </span>
  );
}

function PublicationBadge({ pub }: { pub: Entry['customerPublication'] }) {
  if (!pub) {
    return (
      <span className="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground">
        未生成客户发布记录
      </span>
    );
  }
  const labelStateText = PUB_STATE_LABEL[pub.reviewState] ?? pub.reviewState;
  if (pub.reviewState === 'published' && pub.customerVisible) {
    return (
      <span className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs">
        客户可见：{labelStateText}
      </span>
    );
  }
  if (pub.reviewState === 'retracted') {
    return (
      <span className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-xs">
        已撤回，客户端立即不可见
      </span>
    );
  }
  return (
    <span className="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground">
      {labelStateText}（客户暂不可见）
    </span>
  );
}
